import { useMemo } from "react";
import { parseAsString, useQueryState } from "nuqs";
import { resolveMonitoringMetric } from "../../../shared/utils/monitoring-filters";
import type { GetRequestsMonitoringMetricKey } from "@synthesis/shared-services/internal/application/types";

import type { ColDef, GridApi } from "ag-grid-community";

import { getFilterValues } from "@synthesis/shared-services/internal/application/client";
import { getFilterValuesColumn, type GetFilterValuesColumnKey } from "@synthesis/shared-services/internal/application/types";
import { getApplicantNames, getOperatorFilterValues } from "@synthesis/shared-services/external/application/client";
import { getOperatorFilterValuesColumn } from "@synthesis/shared-services/external/application/types";
import type { FilterValueItem } from "@synthesis/shared-services/external/application/types";
import {
  EMPTY_VALUE_KEY,
  getTableColumnDateFilter,
  getTableColumnListFilter,
  type TableListColumnFilterItem,
  type TableListColumnFilterModel,
} from "@synthesis/shared-ui";

import type { ClientType } from "../../../shared/hooks/use-client-type";
import type { CreateApplicationDictionaries } from "../../create-application-page/create-application-dictionaries.lib";
import {
  ASSIGNEE_UNASSIGNED_FILTER_ITEM,
  DEADLINE_CONTROL_FILTER_OPTIONS,
  EXTERNAL_STATUS_FILTER_OPTIONS,
  INTERNAL_STATUS_FILTER_OPTIONS,
  PAYMENT_FLAG_FILTER_OPTIONS,
  PROCESSING_FLAG_FILTER_OPTIONS,
  REQUEST_SUBJECT_FILTER_OPTIONS,
  getApplicationsTableColumns,
  getSelectedListFilterIds,
  type ApplicationsTableItem,
  type ApplicationsTableVariant,
} from "../applications-page.lib";

const FILTER_VALUES_LIMIT = 100;

type FilterGetItems = (params: { search: string; page: number; signal?: AbortSignal; context?: unknown }) => Promise<TableListColumnFilterItem[]>;

type ListFilterConfig = NonNullable<Parameters<typeof getTableColumnListFilter>[0]>;

const buildRemoteFilterGetItems = (
  apiRef: GridApi<ApplicationsTableItem> | null,
  column: GetFilterValuesColumnKey,
  colId: string,
  options?: { includeUnassigned?: boolean },
  monitoringMetric?: GetRequestsMonitoringMetricKey,
): FilterGetItems => {
  return ({ search, signal }) => {
    const selected = getSelectedListFilterIds(apiRef?.getFilterModel()?.[colId] as TableListColumnFilterModel | undefined);
    const selectedForApi = options?.includeUnassigned ? selected.filter((id) => id !== EMPTY_VALUE_KEY) : selected;

    return getFilterValues({
      query: {
        column,
        q: search,
        limit: FILTER_VALUES_LIMIT,
        selected: selectedForApi,
        monitoringMetric,
      },
      signal,
    }).then(({ data }) => {
      const items = (data ?? []).map((item) => ({ id: item.value ?? "", label: item.displayName ?? "" }));

      if (!options?.includeUnassigned) {
        return items;
      }

      const withoutDuplicateEmpty = items.filter((item) => item.id !== EMPTY_VALUE_KEY && item.id !== "");
      return [ASSIGNEE_UNASSIGNED_FILTER_ITEM, ...withoutDuplicateEmpty];
    });
  };
};

type UseApplicationsTableColumnsOptions = {
  tableVariant: ApplicationsTableVariant;
  clientType: ClientType;
  dictionaries: CreateApplicationDictionaries;
  tableApiRef: GridApi<ApplicationsTableItem> | null;
};

export const useApplicationsTableColumns = ({
  tableVariant,
  clientType,
  dictionaries,
  tableApiRef,
}: UseApplicationsTableColumnsOptions): ColDef<ApplicationsTableItem>[] => {
  const baseColumns = getApplicationsTableColumns(tableVariant);
  const [metricId] = useQueryState("monitoringMetric", parseAsString);
  const monitoringMetric = resolveMonitoringMetric(metricId);

  return useMemo(() => {
    const railwayOptions: TableListColumnFilterItem[] = dictionaries.railways.map((r) => ({
      id: r.value,
      label: r.label,
    }));

    const applyListFilterToColumn = (col: (typeof baseColumns)[number], config: ListFilterConfig) => ({
      ...col,
      ...getTableColumnListFilter<ApplicationsTableItem>(config),
    });

    const applyDateFilterToColumn = (col: (typeof baseColumns)[number]) => ({
      ...getTableColumnDateFilter<ApplicationsTableItem>(),
      ...col,
    });

    const requestSubjectFilterConfig: ListFilterConfig = {
      getItems: () => Promise.resolve(REQUEST_SUBJECT_FILTER_OPTIONS),
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      rowFilter: () => true,
      withSearch: false,
    };

    const externalStatusFilterConfig: ListFilterConfig = {
      getItems: () => Promise.resolve(EXTERNAL_STATUS_FILTER_OPTIONS),
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      withAllSelectCheckbox: true,
      rowFilter: () => true,
    };

    const internalStatusFilterConfig: ListFilterConfig = {
      getItems: () => Promise.resolve(INTERNAL_STATUS_FILTER_OPTIONS),
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      withAllSelectCheckbox: true,
      rowFilter: () => true,
    };

    const railwayFilterConfig: ListFilterConfig = {
      getItems: () => Promise.resolve(railwayOptions),
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      withAllSelectCheckbox: true,
      rowFilter: () => true,
    };

    const paymentFilterConfig: ListFilterConfig = {
      getItems: () => Promise.resolve(PAYMENT_FLAG_FILTER_OPTIONS),
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      rowFilter: () => true,
      withSearch: false,
    };

    const processingFlagFilterConfig: ListFilterConfig = {
      getItems: () => Promise.resolve(PROCESSING_FLAG_FILTER_OPTIONS),
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      rowFilter: () => true,
      withSearch: false,
    };

    const deadlineControlFilterConfig: ListFilterConfig = {
      getItems: () => Promise.resolve(DEADLINE_CONTROL_FILTER_OPTIONS),
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      rowFilter: () => true,
      withSearch: false,
    };

    const remoteFilterConfig = (column: GetFilterValuesColumnKey, colId: string, options?: { includeUnassigned?: boolean }): ListFilterConfig => ({
      getItems: buildRemoteFilterGetItems(tableApiRef, column, colId, options, monitoringMetric),
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      withAllSelectCheckbox: true,
      rowFilter: () => true,
    });

    type ExternalFilterValuesFetcher = (params: { q: string; limit: number; selected: string[]; signal?: AbortSignal }) => Promise<FilterValueItem[]>;

    const externalRemoteFilterConfig = (colId: string, fetcher: ExternalFilterValuesFetcher): ListFilterConfig => ({
      getItems: ({ search, signal }) => {
        const selected = getSelectedListFilterIds(tableApiRef?.getFilterModel()?.[colId] as TableListColumnFilterModel | undefined);
        return fetcher({ q: search, limit: FILTER_VALUES_LIMIT, selected, signal }).then((items) =>
          items.map((item) => ({ id: item.value ?? "", label: item.displayName ?? "" })),
        );
      },
      getItemId: (item) => item.id ?? "",
      getItemLabel: (item) => item.label ?? "",
      withAllSelectCheckbox: true,
      rowFilter: () => true,
    });

    return baseColumns.map((col) => {
      switch (tableVariant) {
        case "operator":
          switch (col.colId) {
            case "importedAt":
            case "processedAt": {
              const column = applyDateFilterToColumn(col);
              return { ...column, sortable: true };
            }
            case "processingFlag": {
              const column = applyListFilterToColumn(col, processingFlagFilterConfig);
              return { ...column, sortable: true };
            }
            case "operator":
              return {
                ...(clientType === "external"
                  ? applyListFilterToColumn(
                      col,
                      externalRemoteFilterConfig("operator", ({ q, limit, selected, signal }) =>
                        getOperatorFilterValues({
                          query: { column: getOperatorFilterValuesColumn.OPERATOR, q, limit, selected },
                          signal,
                        }).then(({ data }) => data ?? []),
                      ),
                    )
                  : applyListFilterToColumn(col, remoteFilterConfig(getFilterValuesColumn.OPERATOR, "operator"))),
                sortable: true,
              };
            case "railway":
              return railwayOptions.length > 0 ? applyListFilterToColumn(col, railwayFilterConfig) : col;
            case "status":
              return clientType === "external" ? { ...col, sortable: true } : col;
            case "initiator":
              if (clientType === "external") {
                return applyListFilterToColumn(
                  col,
                  externalRemoteFilterConfig("initiator", ({ q, limit, selected, signal }) =>
                    getOperatorFilterValues({
                      query: { column: getOperatorFilterValuesColumn.INITIATOR, q, limit, selected },
                      signal,
                    }).then(({ data }) => data ?? []),
                  ),
                );
              }
              return applyListFilterToColumn(col, remoteFilterConfig(getFilterValuesColumn.INITIATOR, "initiator"));
            default:
              return col;
          }
        case "ng-secretary":
          switch (col.colId) {
            case "type":
              return applyListFilterToColumn(col, requestSubjectFilterConfig);
            case "importedAt":
            case "createdAt":
            case "updatedAt":
              return applyDateFilterToColumn(col);
            case "reviewDueDate":
              return { ...applyDateFilterToColumn(col), sortable: true };
            case "status":
              return applyListFilterToColumn(col, internalStatusFilterConfig);
            case "responsible":
              return {
                ...applyListFilterToColumn(col, remoteFilterConfig(getFilterValuesColumn.ASSIGNEE, "responsible", { includeUnassigned: true })),
                sortable: true,
              };
            case "deadlineControl":
              return { ...applyListFilterToColumn(col, deadlineControlFilterConfig), sortable: true };
            case "section":
              return applyListFilterToColumn(col, remoteFilterConfig(getFilterValuesColumn.SECTION, "section"));
            case "payment":
              return { ...applyListFilterToColumn(col, paymentFilterConfig), sortable: true };
            case "applicantName":
              return applyListFilterToColumn(col, remoteFilterConfig(getFilterValuesColumn.APPLICANT_NAME, "applicantName"));
            case "initiator":
              return applyListFilterToColumn(col, remoteFilterConfig(getFilterValuesColumn.INITIATOR, "initiator"));
            default:
              return col;
          }
        default:
          if (clientType === "external") {
            switch (col.colId) {
              case "type":
                return applyListFilterToColumn(col, requestSubjectFilterConfig);
              case "status":
                return applyListFilterToColumn(col, externalStatusFilterConfig);
              case "railway":
                return railwayOptions.length > 0 ? applyListFilterToColumn(col, railwayFilterConfig) : col;
              case "payment":
                return { ...applyListFilterToColumn(col, paymentFilterConfig), sortable: true };
              case "applicantName":
                return applyListFilterToColumn(
                  col,
                  externalRemoteFilterConfig("applicantName", ({ q, limit, selected, signal }) =>
                    getApplicantNames({ query: { q, limit, selected }, signal }).then(({ data }) => data ?? []),
                  ),
                );
              case "createdAt":
              case "updatedAt":
                return applyDateFilterToColumn(col);
              default:
                return col;
            }
          }

          switch (col.colId) {
            case "type":
              return applyListFilterToColumn(col, requestSubjectFilterConfig);
            case "railway":
              return railwayOptions.length > 0 ? applyListFilterToColumn(col, railwayFilterConfig) : col;
            case "payment":
              return { ...col, sortable: true };
            default:
              return col;
          }
      }
    });
  }, [baseColumns, clientType, dictionaries, tableVariant, tableApiRef, monitoringMetric]);
};
